import { z } from 'zod';
import { audienceSpecSchema } from '../audience/index';

/**
 * Contracten van de module agenda: evenementen, toolboxmomenten en aanwezigheid.
 *
 * **De module heet `agenda` en niet `events`** (ADR-0053). In deze codebase betekent "events" al
 * iets anders — de domeinevents van de eventbus, met `contracts/src/events/` als hun thuis. Een
 * module `events` zou `modules/events/public/events.ts` opleveren: de domeinevents van de
 * events-module.
 *
 * Eerste slice van Stap 25 (demoroute, ADR-0052). Wat er wél is: een evenement plannen voor een
 * doelgroep, medewerkers die zeggen of ze komen, en een aanwezigheidslijst die een teamleider op
 * zijn telefoon afvinkt. Wat er nog niet is: check-in met een QR-code, opleidingsgeldigheid met
 * herinneringen, een ICS-download en een maandkalender — zie `docs/product/demo-schuld.md`.
 *
 * Het onderscheid tussen RSVP en aanwezigheid is het punt van deze module. "Ik kom" is een belofte
 * van de medewerker; "aanwezig" is een vaststelling door iemand anders. Bij een toolboxmeeting over
 * veiligheid is dat tweede het enige dat telt, en het eerste helpt alleen om de zaal in te schatten.
 */

/**
 * Drie soorten, en het verschil is niet cosmetisch.
 *
 * Een `toolbox` is een verplicht veiligheidsmoment waarvan de aanwezigheid bewaard moet blijven; een
 * `training` is een opleiding die later een geldigheidsduur krijgt (rest van Stap 25); een `event`
 * is al het andere — een personeelsfeest, een bedrijfsbezoek.
 */
export const agendaItemKindSchema = z.enum(['event', 'toolbox', 'training']);
export type AgendaItemKind = z.infer<typeof agendaItemKindSchema>;

export const agendaItemStatusSchema = z.enum(['draft', 'published', 'cancelled']);
export type AgendaItemStatus = z.infer<typeof agendaItemStatusSchema>;

/** Komt de medewerker? `unknown` bestaat niet als waarde: geen rij betekent geen antwoord. */
export const rsvpAnswerSchema = z.enum(['yes', 'no']);
export type RsvpAnswer = z.infer<typeof rsvpAnswerSchema>;

export const agendaItemSummarySchema = z.object({
  id: z.guid(),
  kind: agendaItemKindSchema,
  status: agendaItemStatusSchema,
  title: z.string(),
  /** Vrije tekst. Geen kaart en geen adrescontrole: "Kantine" of "Hal 3" is wat mensen schrijven. */
  location: z.string(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  rsvpEnabled: z.boolean(),
  /** Na dit tijdstip kan niemand zijn antwoord nog wijzigen; leeg = tot de start. */
  rsvpDeadline: z.string().datetime().nullable(),
  audienceDescription: z.string(),
  recipientCount: z.number().int().nullable(),
  createdAt: z.string().datetime(),
});
export type AgendaItemSummary = z.infer<typeof agendaItemSummarySchema>;

export const agendaItemDetailSchema = agendaItemSummarySchema.extend({
  description: z.string(),
  audience: audienceSpecSchema,
  /** Aantallen, nooit namen — dezelfde regel als op een berichtkaart (ADR-0049). */
  yesCount: z.number().int(),
  noCount: z.number().int(),
  attendanceCount: z.number().int(),
});
export type AgendaItemDetail = z.infer<typeof agendaItemDetailSchema>;

export const agendaItemListSchema = z.object({ items: z.array(agendaItemSummarySchema) });
export type AgendaItemList = z.infer<typeof agendaItemListSchema>;

/**
 * Een evenement zoals de medewerker het op zijn agenda ziet.
 *
 * Met zijn eigen antwoord erbij, zodat de lijst in één aanroep te tekenen is. `attended` staat er
 * ook: wie afgevinkt is, hoort dat te kunnen zien — het is een vaststelling over hem.
 */
export const myAgendaItemSchema = agendaItemSummarySchema.extend({
  myAnswer: rsvpAnswerSchema.nullable(),
  attended: z.boolean(),
});
export type MyAgendaItem = z.infer<typeof myAgendaItemSchema>;

/**
 * Één maand agenda, voor het maandrooster op het beginscherm (UI-8, §10.2).
 *
 * Een maand en niet de hele agenda: het rooster toont zeven kolommen met één maand erin, en wie
 * terugbladert vraagt de vorige maand op. Zonder deze grens zou elk blad van het rooster de
 * volledige agenda van het bedrijf ophalen — en dat is precies de vorm die op een telefoon met
 * tweehonderd evenementen traag wordt.
 *
 * Leeg betekent "wat er nu loopt", de lijst die er sinds Stap 25 al was.
 */
export const myAgendaQuerySchema = z.object({
  /** "2026-09". De grenzen worden in de tijdzone van het bedrijf gelegd, niet in UTC. */
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Geef de maand als JJJJ-MM.')
    .optional(),
});
export type MyAgendaQuery = z.infer<typeof myAgendaQuerySchema>;

/**
 * Een periode voor de beheerlijst (FBK-9): de dag, week of maand die de kalender toont. Zonder
 * periode de lijst van vroeger (alles, eerstvolgende eerst, hoogstens 200). Een evenement telt mee
 * als het de periode raakt, ook als het ervoor begon.
 */
export const agendaItemQuerySchema = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type AgendaItemQuery = z.infer<typeof agendaItemQuerySchema>;

export const myAgendaListSchema = z.object({ items: z.array(myAgendaItemSchema) });
export type MyAgendaList = z.infer<typeof myAgendaListSchema>;

export const createAgendaItemRequestSchema = z
  .object({
    kind: agendaItemKindSchema,
    title: z.string().trim().min(1, 'Geef het evenement een titel.').max(200),
    description: z.string().trim().max(5000).default(''),
    location: z.string().trim().max(200).default(''),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    rsvpEnabled: z.boolean().default(true),
    rsvpDeadline: z.string().datetime().nullable().default(null),
    audience: audienceSpecSchema,
  })
  .refine((value) => new Date(value.endsAt) > new Date(value.startsAt), {
    message: 'Het einde moet na het begin liggen.',
    path: ['endsAt'],
  })
  .refine(
    (value) => !value.rsvpDeadline || new Date(value.rsvpDeadline) <= new Date(value.startsAt),
    {
      // Een deadline ná de start zou betekenen dat je nog kunt afzeggen terwijl de toolbox loopt.
      message: 'De antwoorddeadline moet vóór of op het begin liggen.',
      path: ['rsvpDeadline'],
    },
  );
export type CreateAgendaItemRequest = z.infer<typeof createAgendaItemRequestSchema>;

export const rsvpRequestSchema = z.object({ answer: rsvpAnswerSchema });
export type RsvpRequest = z.infer<typeof rsvpRequestSchema>;

/**
 * Eén regel op de afvinklijst van de teamleider.
 *
 * Hier staan wél namen, en dat is geen uitzondering op ADR-0014: een aanwezigheidslijst van een
 * veiligheidsmoment is per definitie een namenlijst, en ze zit achter het recht
 * `agenda.attendance.record`. Wie dat recht niet heeft, krijgt deze lijst nooit te zien.
 */
export const attendanceRowSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  employeeNumber: z.string().nullable(),
  myAnswer: rsvpAnswerSchema.nullable(),
  attendedAt: z.string().datetime().nullable(),
});
export type AttendanceRow = z.infer<typeof attendanceRowSchema>;

export const attendanceListSchema = z.object({
  items: z.array(attendanceRowSchema),
  /** Of er op dit moment afgevinkt mag worden; buiten het tijdvenster is de lijst alleen-lezen. */
  recordingOpen: z.boolean(),
});
export type AttendanceList = z.infer<typeof attendanceListSchema>;

export const recordAttendanceRequestSchema = z.object({
  personId: z.guid(),
  /** Vinkje aan of uit: een foutief afgevinkte collega moet terug te zetten zijn. */
  present: z.boolean(),
});
export type RecordAttendanceRequest = z.infer<typeof recordAttendanceRequestSchema>;
